import type { LadderNode, LadderView } from "./ladderView";
import type { StudioTree, StudioTreeNode } from "./treeProjection";

export const TREE_NODE_WIDTH = 220;
export const TREE_NODE_HEIGHT = 48;
export const TREE_COLUMN_GAP = 72;
const COLUMN_GAP = TREE_COLUMN_GAP;
const SIBLING_GAP = 12;

export interface StudioTreeLayoutNode {
  node: StudioTreeNode;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface StudioTreeLayout {
  nodes: StudioTreeLayoutNode[];
  edges: { id: string; source: string; target: string }[];
  width: number;
  height: number;
}

export const computeStudioTreeLayout = (
  tree: StudioTree,
  expandedIds: ReadonlySet<string>,
): StudioTreeLayout => {
  const nodes: StudioTreeLayoutNode[] = [];
  const edges: StudioTreeLayout["edges"] = [];
  let maxDepth = 0;
  let cursorY = 0;

  const place = (id: string, depth: number): number | null => {
    // Content-path ids go stale across a rebuild — drop them, do not throw.
    const node = tree.nodesById[id];
    if (!node) return null;
    maxDepth = Math.max(maxDepth, depth);
    const visibleChildIds = expandedIds.has(id)
      ? node.childIds.filter((childId) => tree.nodesById[childId])
      : [];

    let y: number;
    if (visibleChildIds.length === 0) {
      y = cursorY;
      cursorY += TREE_NODE_HEIGHT + SIBLING_GAP;
    } else {
      const childYs = visibleChildIds.flatMap((childId) => {
        const childY = place(childId, depth + 1);
        if (childY === null) return [];
        edges.push({ id: `${id}->${childId}`, source: id, target: childId });
        return [childY];
      });
      if (childYs.length === 0) {
        y = cursorY;
        cursorY += TREE_NODE_HEIGHT + SIBLING_GAP;
      } else {
        y = (childYs[0] + childYs[childYs.length - 1]) / 2;
      }
    }

    nodes.push({
      node,
      x: depth * (TREE_NODE_WIDTH + COLUMN_GAP),
      y,
      width: TREE_NODE_WIDTH,
      height: TREE_NODE_HEIGHT,
    });
    return y;
  };

  for (const rootId of tree.rootIds) {
    place(rootId, 0);
  }

  const order = new Map<string, number>();
  let index = 0;
  const number = (id: string) => {
    if (!tree.nodesById[id]) return;
    order.set(id, index++);
    if (expandedIds.has(id)) {
      for (const childId of tree.nodesById[id].childIds) number(childId);
    }
  };
  for (const rootId of tree.rootIds) number(rootId);
  nodes.sort((a, b) => (order.get(a.node.id) ?? 0) - (order.get(b.node.id) ?? 0));

  return {
    nodes,
    edges,
    width: maxDepth * (TREE_NODE_WIDTH + COLUMN_GAP) + TREE_NODE_WIDTH,
    height: Math.max(cursorY - SIBLING_GAP, 0),
  };
};

export const RUNG_NODE_HEIGHT = 30;

export interface LadderLayoutNode {
  node: LadderNode;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface LadderLayout {
  nodes: LadderLayoutNode[];
  /** Same column as the values — leave the bottom, not the side. */
  edges: { id: string; source: string; target: string; orientation: "across" | "down" }[];
  width: number;
  height: number;
}

/** x is the node's level, never depth — a rung and its values share a column. */
export const computeLadderLayout = (view: LadderView): LadderLayout => {
  const nodes: LadderLayoutNode[] = [];
  const edges: LadderLayout["edges"] = [];
  let cursorY = 0;
  let maxColumn = 0;

  const place = (id: string): number | null => {
    // Stale id degrades, never throws.
    const node = view.nodesById[id];
    if (!node) return null;
    const height = node.kind === "level" ? RUNG_NODE_HEIGHT : TREE_NODE_HEIGHT;
    maxColumn = Math.max(maxColumn, node.column);
    let y: number;

    const liveChildIds = node.childIds.filter((childId) => view.nodesById[childId]);
    if (node.kind === "value") {
      const childYs = liveChildIds.flatMap((childId) => {
        const childY = place(childId);
        if (childY === null) return [];
        edges.push({
          id: `${id}->${childId}`,
          source: id,
          target: childId,
          orientation: "across",
        });
        return [childY];
      });
      if (childYs.length === 0) {
        y = cursorY;
        cursorY += height + SIBLING_GAP;
      } else {
        y = (childYs[0] + childYs[childYs.length - 1]) / 2;
      }
    } else {
      const chainIds = liveChildIds.filter(
        (childId) => view.nodesById[childId].column > node.column,
      );
      const valueIds = liveChildIds.filter(
        (childId) => view.nodesById[childId].column <= node.column,
      );
      const chainYs = chainIds.flatMap((childId) => {
        const childY = place(childId);
        if (childY === null) return [];
        edges.push({
          id: `${id}->${childId}`,
          source: id,
          target: childId,
          orientation: "across",
        });
        return [childY];
      });
      if (chainYs.length > 0) {
        y = chainYs[0];
      } else {
        y = cursorY;
        cursorY += height + SIBLING_GAP;
      }
      for (const childId of valueIds) {
        edges.push({ id: `${id}->${childId}`, source: id, target: childId, orientation: "down" });
        place(childId);
      }
    }

    nodes.push({
      node,
      x: node.column * (TREE_NODE_WIDTH + COLUMN_GAP),
      y,
      width: TREE_NODE_WIDTH,
      height,
    });
    return y;
  };

  for (const rootId of view.rootIds) place(rootId);

  const order = new Map<string, number>();
  let index = 0;
  const number = (id: string) => {
    const node = view.nodesById[id];
    if (!node) return;
    order.set(id, index++);
    for (const childId of node.childIds) number(childId);
  };
  for (const rootId of view.rootIds) number(rootId);
  nodes.sort((a, b) => (order.get(a.node.id) ?? 0) - (order.get(b.node.id) ?? 0));

  return {
    nodes,
    edges,
    width: maxColumn * (TREE_NODE_WIDTH + COLUMN_GAP) + TREE_NODE_WIDTH,
    height: Math.max(cursorY - SIBLING_GAP, 0),
  };
};
